import { Controller } from 'stimulus'
import { Chart, BarController, BarElement, CategoryScale, LinearScale, Tooltip, Legend } from 'chart.js'

Chart.register(BarController, BarElement, CategoryScale, LinearScale, Tooltip, Legend)

// Stats page charts (stats/_charts.haml). Colours come from CSS custom
// properties on .stats-chart (validated palette: downloads blue, plays orange,
// listening time aqua - all >= 3:1 on the card surface, CVD-safe as a set).
//   per-track   - downloads & plays per track (horizontal bars)
//   listen-time - average listening time per track (horizontal bars)
//   over-time   - downloads & plays per day/month (vertical bars)
export default class extends Controller {
  static values = { kind: String, data: Object }

  // data-*-value JSON may be an array (per-track) or object (over-time)
  get payload() {
    return JSON.parse(this.element.dataset.statsChartDataValue)
  }

  connect() {
    const css = getComputedStyle(this.element.closest('.stats-chart'))
    const token = (name) => css.getPropertyValue(name).trim()
    this.colors = {
      downloads: token('--series-downloads'),
      plays: token('--series-plays'),
      listen: token('--series-listen'),
      text: token('--text-secondary'),
      grid: token('--grid'),
      surface: token('--surface')
    }
    Chart.defaults.font.family = getComputedStyle(document.body).fontFamily
    Chart.defaults.font.size = 12
    Chart.defaults.color = this.colors.text

    this.chart = new Chart(this.element, this.config())
  }

  disconnect() {
    this.chart?.destroy()
  }

  config() {
    switch (this.kindValue) {
      case 'listen-time': return this.listenTime()
      case 'over-time': return this.overTime()
      default: return this.perTrack()
    }
  }

  perTrack() {
    const rows = this.payload
    return this.bars({
      horizontal: true,
      labels: rows.map((r) => r.label),
      datasets: [
        this.dataset('Downloads', rows.map((r) => r.downloads), this.colors.downloads),
        this.dataset('Plays', rows.map((r) => r.plays), this.colors.plays)
      ]
    })
  }

  listenTime() {
    const rows = this.payload
    return this.bars({
      horizontal: true,
      legend: false, // one series - the title names it
      labels: rows.map((r) => r.label),
      datasets: [this.dataset('Average listening time', rows.map((r) => r.average_seconds), this.colors.listen)],
      format: formatDuration
    })
  }

  overTime() {
    const d = this.payload
    return this.bars({
      horizontal: false,
      labels: d.labels,
      datasets: [
        this.dataset('Downloads', d.downloads, this.colors.downloads),
        this.dataset('Plays', d.plays, this.colors.plays)
      ]
    })
  }

  dataset(label, data, color) {
    return {
      label,
      data,
      backgroundColor: color,
      hoverBackgroundColor: color,
      // 4px rounded data end, square at the baseline; 2px surface gap between bars
      borderRadius: { topLeft: 4, topRight: 4, bottomLeft: 0, bottomRight: 0 },
      borderSkipped: 'start',
      borderColor: this.colors.surface,
      borderWidth: { top: 0, bottom: 0, left: 1, right: 1 },
      maxBarThickness: 14,
      categoryPercentage: 0.7,
      barPercentage: 0.9
    }
  }

  // Per-track charts: clicking a bar or a track name opens that track's stats
  onClick(event, elements, chart) {
    const rows = this.kindValue === 'over-time' ? null : this.payload
    if (!rows) return
    let index = elements[0]?.index
    if (index === undefined) {
      const y = chart.scales.y
      const { x: px, y: py } = event
      if (px < y.right && py >= y.top && py <= y.bottom) index = y.getValueForPixel(py)
    }
    const url = rows[Math.round(index)]?.url
    if (url) window.Turbolinks ? window.Turbolinks.visit(url) : (window.location.href = url)
  }

  onHover(event, elements, chart) {
    if (this.kindValue === 'over-time') return
    const y = chart.scales.y
    const overLabel = event.x < y.right && event.y >= y.top && event.y <= y.bottom
    chart.canvas.style.cursor = (elements.length || overLabel) ? 'pointer' : 'default'
  }

  bars({ horizontal, labels, datasets, legend = true, format = (v) => v.toLocaleString() }) {
    const valueAxis = horizontal ? 'x' : 'y'
    const categoryAxis = horizontal ? 'y' : 'x'
    const radius = (horizontal ? { topRight: 4, bottomRight: 4, topLeft: 0, bottomLeft: 0 } : null)
    if (radius) datasets.forEach((ds) => { ds.borderRadius = radius; ds.borderWidth = { top: 1, bottom: 1, left: 0, right: 0 } })

    return {
      type: 'bar',
      data: { labels, datasets },
      options: {
        indexAxis: horizontal ? 'y' : 'x',
        responsive: true,
        maintainAspectRatio: false,
        animation: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? false : { duration: 300 },
        interaction: { mode: 'index', intersect: false, axis: categoryAxis },
        onClick: (event, elements, chart) => this.onClick(event, elements, chart),
        onHover: (event, elements, chart) => this.onHover(event, elements, chart),
        plugins: {
          legend: {
            display: legend,
            position: 'top',
            align: 'start',
            labels: { boxWidth: 12, boxHeight: 12, useBorderRadius: true, borderRadius: 2, padding: 16 }
          },
          tooltip: {
            backgroundColor: '#0a0a0a',
            titleColor: '#f5f5f5',
            bodyColor: '#f5f5f5',
            borderColor: this.colors.grid,
            borderWidth: 1,
            padding: 10,
            boxPadding: 4,
            callbacks: { label: (ctx) => ` ${ctx.dataset.label}: ${format(ctx.parsed[valueAxis])}` }
          }
        },
        scales: {
          [valueAxis]: {
            beginAtZero: true,
            grid: { color: this.colors.grid, drawBorder: false },
            ticks: { precision: 0, callback: (v) => format(v), maxTicksLimit: 6 }
          },
          [categoryAxis]: {
            grid: { display: false, drawBorder: false },
            ticks: {
              autoSkip: !horizontal,
              maxRotation: 0,
              // Long track names are truncated on the axis; the tooltip has them in full
              callback(value) {
                const label = this.getLabelForValue(value)
                return horizontal && label.length > 28 ? `${label.slice(0, 27)}…` : label
              }
            }
          }
        }
      }
    }
  }
}

function formatDuration(seconds) {
  const s = Math.round(seconds || 0)
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  if (h) return `${h}h ${m}m`
  if (m) return `${m}m ${s % 60}s`
  return `${s}s`
}
